import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import { getMicrophoneStream, getRecorderOptions, createRunningAudioContext, waitForLiveAudioTrack } from "../utils/speechUtils";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import { ButtonBlue, ButtonYellow } from "./buttons";

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(0, 3),
  },
  title: {
    fontWeight: "bold",
  },
  instructions: {
    marginBottom: theme.spacing(2),
    width: "100%",
    textAlign: "left",
    fontSize: "1.25rem",
  },
  sentance: {
    fontSize: "1.25rem",
    marginBottom: theme.spacing(2),
  },
  recordButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    width: "100%",
  },
  audioPlayerContainer: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
  audioPlayer: {
    width: "100%",
  },
  statusMessageContainer: {
    marginTop: theme.spacing(4),
  },
  failedText: {
    color: "rgba(255, 0, 0, 1)",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
  },
  successText: {
    color: "rgba(0, 255, 0, 1)",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
  },
  icons: {
    height: "2rem",
    width: "2rem",
  },
}));

const SILENCE_THRESHOLD = 8;
const SILENCE_DURATION_MS = 3 * 1000; // 3 seconds in milliseconds
const SPEECH_AMPLITUDE_THRESHOLD = 20;

const MicrophoneChecker = ({ forceOpen = false, sampleRate, channelCount }) => {
  const { classes } = useStyles();
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [speechDetected, setSpeechDetected] = useState(false);
  const [evaluated, setEvaluated] = useState(false);
  const [open, setOpen] = useState(forceOpen);

  const audioPlayerRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const streamRef = useRef(null);
  // One live recording: { recorder, stream, chunks, aborted }. Chunks are
  // per-recording — only the first carries the container header, so a buffer
  // shared between recorders produces playback that cannot be decoded.
  const activeSessionRef = useRef(null);
  // Covers the window between the click and getUserMedia resolving.
  const startingRef = useRef(false);
  const animationFrameIdRef = useRef(null);
  const volumeAnimateFrameIdRef = useRef(null);

  const drawWaveform = () => {
    if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);

      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      canvasCtx.fillStyle = "#fff";
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = "#4e36f5";

      canvasCtx.beginPath();
      const sliceWidth = canvas.width / dataArray.length;
      let x = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();

      requestAnimationFrame(draw);
    };

    draw();
  };

  const startRecording = () => {
    if (isRecording) return stopRecording();
    if (startingRef.current) return;
    startingRef.current = true;

    getMicrophoneStream()
      .then(async (stream) => {
        // Retire any recorder this one supersedes; left running it holds the mic
        // open and keeps emitting chunks.
        const previous = activeSessionRef.current;
        if (previous) {
          previous.aborted = true;
          try {
            if (previous.recorder.state !== "inactive") previous.recorder.stop();
          } catch (e) {
            /* already stopped */
          }
          previous.stream.getTracks().forEach((track) => track.stop());
        }

        streamRef.current = stream;
        let silenceStart = null;
        await waitForLiveAudioTrack(stream); // post-idle tracks can start muted
        // Resumed context: a suspended one (idle/backgrounded tab) feeds the
        // analyser silence and wrongly trips the "no audio" detector.
        const audioContext = await createRunningAudioContext({ sampleRate });
        const source = audioContext.createMediaStreamSource(stream);
        const merger = audioContext.createChannelMerger(channelCount);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(merger);
        merger.connect(destination);
        const recorder = new MediaRecorder(destination.stream, getRecorderOptions());
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);

        analyser.fftSize = 2048;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        const detectSilence = () => {
          analyser.getByteTimeDomainData(dataArray);

          // Calculate the average amplitude of the audio signal
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += Math.abs(dataArray[i] - 128);
          }
          const average = sum / bufferLength;

          if (average < SILENCE_THRESHOLD) {
            if (silenceStart === null) {
              silenceStart = Date.now();
            } else if (Date.now() - silenceStart > SILENCE_DURATION_MS) {
              console.log("Silence detected, stopping recording.");

              recorder.stop();
              setIsRecording(false);
              setElapsedTime(0);

              return;
            }
          } else {
            silenceStart = null;
          }

          animationFrameIdRef.current = requestAnimationFrame(detectSilence);
        };

        const analyzeVolume = () => {
          if (analyserRef.current) {
            analyserRef.current.getByteTimeDomainData(dataArrayRef.current);
            const amplitude = Math.floor(
              dataArrayRef.current.reduce(
                (sum, value) => sum + Math.abs(value - 128),
                0,
              ) / dataArrayRef.current.length,
            );
            if (amplitude > SPEECH_AMPLITUDE_THRESHOLD) {
              // Adjust threshold based on noise levels
              setSpeechDetected(true);
            }
          }
          volumeAnimateFrameIdRef.current =
            requestAnimationFrame(analyzeVolume);
        };

        drawWaveform();
        detectSilence();
        analyzeVolume();

        // Chunks belong to THIS recording.
        const chunks = [];
        const session = { recorder, stream, chunks, aborted: false };
        activeSessionRef.current = session;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        recorder.onstop = () => {
          stream.getTracks().forEach((track) => track.stop());

          // onstop fires asynchronously, so a SUPERSEDED session would otherwise
          // cancel the newer recording's animation frames (shared refs).
          const isCurrent = activeSessionRef.current === session;
          if (isCurrent) {
            activeSessionRef.current = null;
            if (animationFrameIdRef.current) {
              cancelAnimationFrame(animationFrameIdRef.current);
            }
            if (volumeAnimateFrameIdRef.current) {
              cancelAnimationFrame(volumeAnimateFrameIdRef.current);
            }
          }

          // Superseded or aborted: release the hardware, publish nothing.
          if (session.aborted || !isCurrent) return;

          const blob = new Blob(chunks, {
            type: recorder.mimeType || "audio/webm",
          });
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob)); // Set the audio URL for playback (if required)
          audioPlayerRef.current?.load();

          setEvaluated(true);
        };
        recorder.start(100); // Collect 100ms chunks of audio
        setIsRecording(true);
        setElapsedTime(0);
        setAudioBlob(null);
        setEvaluated(false);
        setSpeechDetected(false);
      })
      .catch((error) =>
        toast.error("Error accessing microphone: " + error.message),
      )
      .finally(() => {
        startingRef.current = false;
      });
  };

  const stopRecording = () => {
    const session = activeSessionRef.current;
    if (!session) return;
    try {
      if (session.recorder.state !== "inactive") session.recorder.stop();
    } catch (e) {
      /* already stopped */
    }
    setIsRecording(false);
    setElapsedTime(0);
  };

  useEffect(() => {
    try {
      if (!JSON.parse(localStorage.getItem("micCheckPerformed"))) {
        setOpen(true);

        if (!speechDetected) return;
        localStorage.setItem("micCheckPerformed", "true");
      }
    } catch (e) {
      setOpen(true);
    }
  }, [speechDetected]);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        setOpen(false);
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      <Box
        sx={{
          px: 2,
          minHeight: "440px",
          justifyContent: "center",
          flexDirection: "column",
          display: "flex",
        }}
      >
        <DialogTitle className={classes.title}>
          <Typography sx={{ fontWeight: 700 }} variant="h3">
            Test Microphone
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography className={classes.instructions}>
            Click 'Record', speak into your microphone, and then read the
            following sentence.
          </Typography>
          <i className={classes.sentance}>"Hello, I like red apples."</i>
          {speechDetected && evaluated ? (
            <Box className={classes.audioPlayerContainer}>
              <audio
                ref={audioPlayerRef}
                src={audioUrl}
                controls
                className={classes.audioPlayer}
              />
            </Box>
          ) : (
            <ButtonBlue
              buttonProps={{
                variant: isRecording ? "outlined" : "contained",
                className: classes.recordButton,
                onClick: startRecording,
                startIcon: isRecording ? (
                  <StopIcon className={classes.icons} />
                ) : (
                  <MicIcon className={classes.icons} />
                ),
              }}
              style={{ background: "none", width: "100%", padding: 0 }}
            >
              <Typography
                sx={{
                  fontWeight: "700",
                  fontFamily: '"Quicksand", sans-serif',
                  fontSize: {
                    xs: "1em",
                    sm: "1.2em",
                    md: "1.4em",
                    lg: "1.6em",
                  },
                  zIndex: 2,
                }}
              >
                {isRecording ? "Stop" : "Record"}
              </Typography>
            </ButtonBlue>
          )}
          {evaluated && (
            <Box className={classes.statusMessageContainer}>
              {!isRecording && !speechDetected ? (
                <Typography variant="h5" className={classes.failedText}>
                  <Cancel sx={{ mr: 1 }} className={classes.icons} /> Oops! We
                  couldn't hear that. Please retry.
                </Typography>
              ) : speechDetected ? (
                <Typography variant="h5" className={classes.successText}>
                  <CheckCircle sx={{ mr: 1 }} className={classes.icons} /> We
                  heard you!
                </Typography>
              ) : null}
            </Box>
          )}
          <canvas
            ref={canvasRef}
            style={{
              display: isRecording ? "block" : "none",
              width: "100%",
              height: "100px",
            }}
          ></canvas>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          {speechDetected && evaluated && (
            <ButtonYellow
              buttonProps={{
                onClick: () => setOpen(false),
                style: { paddingLeft: "32px", paddingRight: "32px" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: "700",
                  fontFamily: '"Quicksand", sans-serif',
                  fontSize: {
                    xs: "1em",
                    sm: "1.2em",
                    md: "1.4em",
                    lg: "1.6em",
                  },
                  zIndex: 2,
                }}
              >
                Close
              </Typography>
            </ButtonYellow>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default MicrophoneChecker;
